// seenOnScreen — has the person on a share link had an office message in front of them?
// ---------------------------------------------------------------------------
// v0.280.0 (2026-10-30) — Brett: "Yes to the seen marks". The client's report
// page tells the server (/api/public-seen) when the office's newest message on
// an item has been on screen for a moment with the page in front. The office
// then sees "Seen by <name>" under it, and Send update does not email it again.
//
// "On screen" = at least half of the message in view (or 120 px of a tall one),
// the page visible, for DWELL_MS without a break. A glance while scrolling past
// does not count; a hidden tab never counts. Without IntersectionObserver the
// message counts as in view and only the page being in front is timed.

export const DWELL_MS = 1500;

/** The newest office message on an item (the only kind a seen mark counts), or null. */
export function newestOfficeId(comments) {
  let best = null;
  for (const c of comments || []) {
    if (c && c.authorRole === 'author' && Number.isInteger(c.id) && (best == null || c.id > best)) best = c.id;
  }
  return best;
}

/** The message to report for an item, or null when the page has already reported it (or newer). */
export function toReport(comments, known) {
  const id = newestOfficeId(comments);
  return id != null && id > (Number(known) || 0) ? id : null;
}

const inView = (e) => e.isIntersecting && (e.intersectionRatio >= 0.5 || e.intersectionRect.height >= 120);

/**
 * Calls done() once, after el has been in view with the page visible for ms.
 * Returns a stop function. `env` is for tests (window, document, timers).
 */
export function watchSeen(el, done, { ms = DWELL_MS, env = globalThis } = {}) {
  const doc = env.document;
  const IO = env.IntersectionObserver;
  let seen = !IO;
  let timer = null;
  let over = false;
  const visible = () => !doc || doc.visibilityState === 'visible';
  function check() {
    if (over) return;
    if (seen && visible()) {
      if (timer == null) timer = env.setTimeout(() => { timer = null; if (!over && seen && visible()) { stop(); done(); } }, ms);
    } else if (timer != null) {
      env.clearTimeout(timer);
      timer = null;
    }
  }
  const io = IO ? new IO((entries) => {
    for (const e of entries) if (e.target === el) seen = inView(e);
    check();
  }, { threshold: [0, 0.25, 0.5, 1] }) : null;
  if (io) io.observe(el);
  if (doc) doc.addEventListener('visibilitychange', check);
  function stop() {
    over = true;
    if (timer != null) { env.clearTimeout(timer); timer = null; }
    if (io) io.disconnect();
    if (doc) doc.removeEventListener('visibilitychange', check);
  }
  check();
  return stop;
}
